// staff-notes.js - Module 6, the director's feedback-notes composer + thread
// for one coach. Writing is a 30-second action: category → two sentences
// (typed or dictated) → Send or Private. Sent notes appear on the coach's
// hub feed with mark-as-seen; private ones stay director-only in the same
// timeline and feed the "since last review" panel of the next evaluation.

import { DB } from './db.js';
import { DICTATION } from './dictation.js';
import { t } from './strings.js';
import { UI } from './ui.js';
import { UTILS } from './utils.js';

export const STAFF_NOTES = (() => {

  let _person   = null;
  let _notes    = [];
  let _category = 'general';
  let _onBack   = null;

  const CATEGORIES = [
    { key: 'match',    label: () => t('snote.catMatch') },
    { key: 'training', label: () => t('snote.catTraining') },
    { key: 'focus',    label: () => t('snote.catFocus') },
    { key: 'general',  label: () => t('snote.catGeneral') },
  ];

  async function show(person, onBack) {
    _person = person;
    if (onBack) _onBack = onBack;
    UI.showScreen('screen-staff-notes');
    document.getElementById('staff-notes-title').textContent =
      t('snote.title', { name: person.full_name });
    UI.setLoading('staff-notes-content', t('common.loading'));
    try {
      _notes = await DB.staffHub.listNotesFor(person.id) || [];
      _render();
    } catch (err) {
      console.error('Notes load failed:', err);
      UI.setEmpty('staff-notes-content', t('common.error'));
    }
  }

  function backFrom() {
    if (_onBack) _onBack();
  }

  function _render() {
    const el = document.getElementById('staff-notes-content');
    if (!el) return;
    const esc = UTILS.escapeHtml;

    const thread = _notes.length
      ? _notes.map(n => `
          <div class="snote-row ${n.visibility === 'private' ? 'snote-row--private' : ''}">
            <div class="snote-row__meta">
              ${esc(UTILS.formatDate(n.created_at, 'medium'))} · ${esc(CATEGORIES.find(c => c.key === n.category)?.label() || n.category)}
              ${n.visibility === 'private'
                ? `<span class="snote-private-tag">${esc(t('snote.privateTag'))}</span>`
                : n.seen_at
                  ? `<span class="player-read-chip">${esc(t('snote.seenTag', { date: UTILS.formatDate(n.seen_at, 'medium') }))}</span>`
                  : `<span class="player-unread-chip">${esc(t('snote.unseenTag'))}</span>`}
              <button class="sel-roster-btn sel-roster-btn--danger" data-note-del="${esc(n.id)}" style="margin-left:auto;">🗑</button>
            </div>
            <div>${esc(n.body)}</div>
          </div>`).join('')
      : `<div class="sel-detail-hint">${esc(t('snote.none'))}</div>`;

    el.innerHTML = `
      <div class="sel-detail-section">
        <div class="sel-section-toggles">
          ${CATEGORIES.map(c => `
            <button class="sel-section-toggle ${_category === c.key ? 'sel-section-toggle--on' : ''}"
                    data-note-cat="${c.key}">${esc(c.label())}</button>`).join('')}
        </div>
        <textarea class="prompt-input" id="staff-note-body" rows="3"
                  placeholder="${esc(t('snote.placeholder'))}"></textarea>
        <div class="sel-detail-actions" style="margin-top:8px;">
          <button class="btn btn--primary"   id="staff-note-send">${esc(t('snote.send'))}</button>
          <button class="btn btn--secondary" id="staff-note-private">${esc(t('snote.private'))}</button>
        </div>
      </div>
      ${thread}
    `;

    el.querySelectorAll('[data-note-cat]').forEach(b =>
      b.addEventListener('click', () => {
        _category = b.dataset.noteCat;
        const body = document.getElementById('staff-note-body')?.value || '';
        _render();
        const ta = document.getElementById('staff-note-body');
        if (ta) ta.value = body;
      }));

    // Post-match feedback is spoken more often than typed - wire the existing
    // mic → transcription pipeline straight into the composer.
    const ta = document.getElementById('staff-note-body');
    if (ta) DICTATION.attach(ta, {});

    document.getElementById('staff-note-send')
      ?.addEventListener('click', () => _save('sent'));
    document.getElementById('staff-note-private')
      ?.addEventListener('click', () => _save('private'));

    el.querySelectorAll('[data-note-del]').forEach(b =>
      b.addEventListener('click', () => _confirmDelete(b.dataset.noteDel)));
  }

  async function _save(visibility) {
    const ta = document.getElementById('staff-note-body');
    const body = (ta?.value || '').trim();
    if (!body) { UI.toast(t('snote.emptyBody'), 'info'); return; }
    try {
      await DB.staffHub.createNote({
        staff_id: _person.id, category: _category, body, visibility,
      });
      UI.toast(visibility === 'sent'
        ? t('snote.sent', { name: _person.full_name })
        : t('snote.savedPrivate'), 'success', 1500);
      _notes = await DB.staffHub.listNotesFor(_person.id) || [];
      _render();
    } catch (err) {
      console.error('Note save failed:', err);
      UI.toast(t('common.error'), 'error');
    }
  }

  function _confirmDelete(id) {
    UI.modal({
      title: t('snote.deleteTitle'),
      body:  t('snote.deleteBody'),
      buttons: [
        { label: '🗑', style: 'primary', action: async () => {
          try {
            await DB.staffHub.deleteNote(id);
            _notes = _notes.filter(n => n.id !== id);
            _render();
          } catch (err) {
            console.error('Note delete failed:', err);
            UI.toast(t('common.error'), 'error');
          }
        } },
        { label: 'Cancel' },
      ],
    });
  }

  return { show, backFrom };

})();
